import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  auth,
  configureApi,
  setAuthToken,
  clearAuthToken,
  mapApiUserToProfile,
  type UserProfile,
  type CheckMobileResponse,
  type RegisterPayload,
  type CompleteRegisterPayload,
} from '../services/api';
import { getPlatformStore } from '../platform';
import { RegistrationIncompleteError } from '../utils/authErrors';
import {
  enrichCoachAthletes,
  mergeUserProfiles,
  parseApiUserPayload,
  sanitizeUserForStore,
  storedRecordToProfile,
} from '../utils/userProfile';
import { onAuthLogoutPushCleanup } from '../bootstrap/PushMessagingBootstrap';

interface AuthState {
  token: string | null;
  user: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthActions {
  login: (mobile: string, password: string) => Promise<void>;
  loginByOtp: (mobile: string, otp: string) => Promise<void>;
  sendOtp: (mobile: string) => Promise<void>;
  logout: () => Promise<void>;
  checkMobile: (mobile: string) => Promise<{ exists: boolean; completed: boolean }>;
  register: (payload: RegisterPayload) => Promise<void>;
  completeRegister: (payload: CompleteRegisterPayload, token: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export type AuthContextValue = AuthState & AuthActions;

const AuthContext = createContext<AuthContextValue | null>(null);

function getHttpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** Coalesce simultaneous `/me` calls (StrictMode double-mount, parallel hook instances). */
let inflightProfileFetch: Promise<UserProfile | null> | null = null;

/** Loads and merges the authenticated profile from `/me` (+ coach athletes fallback). */
async function resolveProfileFromApi(
  previous: UserProfile | null,
): Promise<UserProfile | null> {
  if (inflightProfileFetch) {
    return inflightProfileFetch;
  }

  inflightProfileFetch = (async () => {
    try {
      const res = await auth.me();
      const apiUser = parseApiUserPayload(res.data.data);
      let mapped = mapApiUserToProfile(apiUser);
      mapped = mergeUserProfiles(previous, mapped);
      mapped = await enrichCoachAthletes(mapped);
      return mapped;
    } finally {
      inflightProfileFetch = null;
    }
  })();

  return inflightProfileFetch;
}

/** Single app-wide auth state — one session restore, one `/me` on boot. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const userRef = useRef<UserProfile | null>(null);
  const restoreStartedRef = useRef(false);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    void getPlatformStore().getApiUrl().then((url) => {
      configureApi(url, token ?? undefined);
    });
  }, [token]);

  const persistUser = useCallback(async (profile: UserProfile) => {
    await getPlatformStore().setUser(sanitizeUserForStore(profile));
  }, []);

  const applyFreshProfile = useCallback(async (previous: UserProfile | null) => {
    const mapped = await resolveProfileFromApi(previous);
    if (!mapped) return;
    const merged = mergeUserProfiles(previous, mapped);
    setUser(merged);
    await persistUser(merged);
  }, [persistUser]);

  // Restore session once on app boot — hydrate from disk, then refresh from API.
  useEffect(() => {
    if (restoreStartedRef.current) return;
    restoreStartedRef.current = true;

    async function restore() {
      try {
        const store = getPlatformStore();
        const [savedToken, apiUrl, storedUserRaw] = await Promise.all([
          store.getToken(),
          store.getApiUrl(),
          store.getUser(),
        ]);

        const storedUser = storedRecordToProfile(storedUserRaw);

        if (apiUrl) configureApi(apiUrl, savedToken || undefined);

        if (!savedToken) {
          return;
        }

        setAuthToken(savedToken);
        setToken(savedToken);

        if (storedUser) {
          setUser(storedUser);
        }

        try {
          const mapped = await resolveProfileFromApi(storedUser);
          if (mapped) {
            setUser(mapped);
            await persistUser(mapped);
          }
        } catch (error) {
          const status = getHttpStatus(error);
          if (status === 401 || status === 403) {
            await store.clearToken();
            clearAuthToken();
            setToken(null);
            setUser(null);
          } else if (storedUser) {
            const enriched = await enrichCoachAthletes(storedUser);
            setUser(enriched);
            await persistUser(enriched);
          } else {
            await store.clearToken();
            clearAuthToken();
            setToken(null);
            setUser(null);
          }
        }
      } finally {
        setIsLoading(false);
      }
    }

    void restore();
  }, [persistUser]);

  async function persistAuth(newToken: string, apiUser: Parameters<typeof mapApiUserToProfile>[0]) {
    setAuthToken(newToken);
    const mapped = mapApiUserToProfile(apiUser);
    const store = getPlatformStore();
    await store.setToken(newToken);
    await store.setUser(sanitizeUserForStore(mapped));
    setToken(newToken);
    setUser(mapped);
  }

  const refreshProfileAfterLogin = useCallback(async (newToken: string) => {
    try {
      setAuthToken(newToken);
      await applyFreshProfile(userRef.current);
    } catch {
      // Non-critical — continue with partial profile
    }
  }, [applyFreshProfile]);

  const login = useCallback(async (mobile: string, password: string) => {
    const res = await auth.loginWithPassword({ mobile, password });
    const raw = res.data.data;
    const newToken = raw.token;
    const apiUser = raw.data.user;

    if (!apiUser.is_register_completed) {
      throw new RegistrationIncompleteError(mobile, newToken);
    }

    await persistAuth(newToken, apiUser);
    await refreshProfileAfterLogin(newToken);
  }, [refreshProfileAfterLogin]);

  const loginByOtp = useCallback(async (mobile: string, otp: string) => {
    const res = await auth.loginByOtp({ mobile, otp });
    const raw = res.data.data;
    const newToken = raw.token;
    await persistAuth(newToken, raw.data.user);
    await refreshProfileAfterLogin(newToken);
  }, [refreshProfileAfterLogin]);

  const sendOtp = useCallback(async (mobile: string) => {
    await auth.sendOtp(mobile);
  }, []);

  const logout = useCallback(async () => {
    await onAuthLogoutPushCleanup();
    try {
      await auth.logout();
    } catch {
      // ignore logout errors
    }
    clearAuthToken();
    await getPlatformStore().clearToken();
    await getPlatformStore().setSelectedAthleteId(null);
    setToken(null);
    setUser(null);
  }, []);

  const checkMobile = useCallback(async (mobile: string) => {
    const res = await auth.checkMobile(mobile);
    const data: CheckMobileResponse = res.data.data;
    return {
      exists: !!data.is_user_exists,
      completed: !!data.is_register_completed,
    };
  }, []);

  const register = useCallback(async (payload: RegisterPayload) => {
    const res = await auth.register(payload);
    const raw = res.data.data;
    const newToken = raw.token;
    await persistAuth(newToken, raw.data.user);
    await refreshProfileAfterLogin(newToken);
  }, [refreshProfileAfterLogin]);

  const completeRegister = useCallback(async (payload: CompleteRegisterPayload, tokenValue: string) => {
    setAuthToken(tokenValue);
    const res = await auth.completeRegister(payload);
    const apiUser = res.data.data.user;
    await persistAuth(tokenValue, apiUser);
    await refreshProfileAfterLogin(tokenValue);
  }, [refreshProfileAfterLogin]);

  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  const refreshProfile = useCallback(async () => {
    if (!token) return;
    if (refreshInFlightRef.current) {
      await refreshInFlightRef.current;
      return;
    }

    setAuthToken(token);
    const task = (async () => {
      try {
        await applyFreshProfile(userRef.current);
      } catch {
        // Keep cached profile on transient network errors
      }
    })();

    refreshInFlightRef.current = task;
    try {
      await task;
    } finally {
      refreshInFlightRef.current = null;
    }
  }, [token, applyFreshProfile]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      isLoading,
      isAuthenticated: !!token,
      login,
      loginByOtp,
      sendOtp,
      logout,
      checkMobile,
      register,
      completeRegister,
      refreshProfile,
    }),
    [
      token,
      user,
      isLoading,
      login,
      loginByOtp,
      sendOtp,
      logout,
      checkMobile,
      register,
      completeRegister,
      refreshProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Shared auth state — must be used inside `<AuthProvider>`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
